import { useEffect, useState, useCallback, useMemo } from 'react';
import PageForm from 'components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import StyledInput from '../../components/styledInput/StyledInput';
import { CardIdentity, Forklift, SearchLoop } from '../../assets/icons';
import ApiDatabase from 'server';
import useContext from '../../hooks/useContext';
import EmptyState from '../../components/EmptyState/EmptyState';
import Pagination from '../../components/pagination/pagination';
import IdentityValidationMobileCard from '../../components/card/MobileListCard/IdentityValidationMobileCard';
import LicenceValidationMobileCard from '../../components/card/MobileListCard/LicenceValidationMobileCard';
import ValidatedDocumentCard from '../../components/card/MobileListCard/ValidatedDocumentCard';
import PillTabs from '../../components/tabs/PillTabs';
import DocumentTypeSelect from '../../components/DocumentTypeSelect/DocumentTypeSelect';
import useSlideOver from '../../components/SlideOver/useSlideOver';
import useModal from '../../components/modal/useModal';
import LeavingItem from '../../components/LeavingItem/LeavingItem';
import useLeavingItems from '../../hooks/useLeavingItems';
import useScrolledShadow from '../../hooks/useScrolledShadow';
import useIdentityValidationParams from '../../hooks/useIdentityValidationParams';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useLicenceValidationList from '../../hooks/useLicenceValidationList';
import { capitalizeFirst } from '../../helper/helper';
import { DOC_TYPES, DOC_LABELS, MODAL_MAP, countIdentityDocumentProgress } from '../../helper/identityDocuments';
import {
  countComplementaryDocumentsWithStatus,
  getDisplayableComplementaryDocuments,
  hasPendingComplementaryDocument,
} from '../../helper/visaComplementaryDocuments';
import {
  applyLicenceDecision,
  fillTemplate,
  getLicenceLabel,
  isLicencePending,
} from '../../helper/licenceValidation';

const LIMIT = 20;
const LICENCE_VERIFICATION_MODAL = 'ad_file_accept_licence';
const SEARCH_DELAY_MS = 300;

const getTempWorkerName = (tw) => {
  const user = tw?.user || tw;
  return `${capitalizeFirst(user?.firstname)} ${capitalizeFirst(user?.lastname)}`.trim() || 'N/A';
};

const isValidTempWorker = (tw) => {
  if (!tw) return false;
  const user = tw?.user || tw;
  return [user?.firstname, user?.lastname, user?.email].some(f => (f || '').toString().trim().length > 0);
};

const hasDocWithStatus = (tw, status) =>
  DOC_TYPES.some(t => Number(tw?.[t]?.validated) === status);

const countDocsWithStatus = (tw, status) =>
  DOC_TYPES.filter(t => Number(tw?.[t]?.validated) === status).length;

// A residence permit extension proof waiting for Jobbiz puts the temp worker in
// the pending list even when every identity file is already handled.
const isPendingForJobbiz = (tw) =>
  hasDocWithStatus(tw, 1) || hasPendingComplementaryDocument(tw);

const countPendingDocs = (tw) =>
  countDocsWithStatus(tw, 1) + countComplementaryDocumentsWithStatus(tw, '1');

const searchMatch = (tw, query) => {
  const user = tw?.user || tw;
  const f = [user?.firstname, user?.lastname, user?.email, user?.phone].map(v => (v || '').toLowerCase());
  return [...f, `${f[0]} ${f[1]}`].some(v => v.includes(query));
};

// ── Skeleton ──

const CardSkeleton = () => (
  <div className="flex flex-col gap-3 p-4 md:p-6 lg:p-8">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="h-5 skeleton-bar w-32" />
          <div className="h-6 skeleton-bar w-12" />
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="h-4 skeleton-bar w-40" />
          <div className="h-4 skeleton-bar w-28" />
        </div>
      </div>
    ))}
  </div>
);

// ── Component ──

const IdentityValidation = () => {
  const { type, tab: activeTab, setType, setTab } = useIdentityValidationParams();
  const isLicence = type === 'licence';
  const [pendingWorkers, setPendingWorkers] = useState([]);
  const [validatedWorkers, setValidatedWorkers] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isWaiting, setIsWaiting] = useState(true);
  const [isWaitingValidated, setIsWaitingValidated] = useState(true);

  const {
    language,
    updateTempWorker, setUpdateTempWorker, setObjID,
    setAdminTempWorkerId, setAdminUserId, setAdminType,
    setLicenceVerificationTempWorker, setLicenceVerificationLicence,
    licenceVerificationDecision, setLicenceVerificationDecision,
  } = useContext();
  const text = (key, fallback = '') =>
    language?.getValue ? language.getValue(`licenceValidation.${key}`, fallback) : fallback;
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const token = localStorage.getItem('xsrfToken');
  const { leave, isLeaving, onLeaveEnd } = useLeavingItems();
  const { ref: listScrollRef, isScrolled: isListScrolled } = useScrolledShadow();

  // ── Data fetching ──

  const getTempWorkers = useCallback(() => {
    setIsWaiting(true);
    ApiDatabase.getAllUsersWithIdentityFilesToComplete(
      { token },
      (data) => {
        setPendingWorkers((data || []).filter(tw => isValidTempWorker(tw) && isPendingForJobbiz(tw)));
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.error(err); setIsWaiting(false); }
    );
  }, [token]);

  const getValidatedWorkers = useCallback(() => {
    setIsWaitingValidated(true);
    ApiDatabase.getTempWorkersValidatedByAI(
      { token },
      (data) => {
        setValidatedWorkers((data || []).filter(tw => isValidTempWorker(tw) && DOC_TYPES.some(t => tw?.[t]?.validatedBy === 'ai')));
        setIsWaitingValidated(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.error(err); setIsWaitingValidated(false); }
    );
  }, [token]);

  // The identity lists are not paginated by the api: they load only while the identity type is shown.
  useEffect(() => {
    if (isLicence) return;
    getTempWorkers();
    getValidatedWorkers();
  }, [isLicence, getTempWorkers, getValidatedWorkers]);

  // ── Licences and CACES: searched and paginated by the api ──

  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DELAY_MS);
  const licenceList = useLicenceValidationList({
    enabled: isLicence,
    tab: activeTab,
    page: currentPage,
    limit: LIMIT,
    search: debouncedSearch,
  });
  const { setRows: setLicenceRows, refreshCounts: refreshLicenceCounts } = licenceList;

  // ── Flatten validated workers → 1 row per AI-validated document ──

  const validatedDocRows = useMemo(() => {
    const rows = [];
    validatedWorkers.forEach(tw => {
      const name = getTempWorkerName(tw);
      DOC_TYPES.forEach(type => {
        const doc = tw?.[type];
        if (!doc || doc.validatedBy !== 'ai') return;
        let label = DOC_LABELS[type];
        if (type === 'identity' && tw.identity?.type) {
          label = tw.identity.type === 'CNI' ? 'Carte d\'identité' : 'Passeport';
        }
        rows.push({
          key: `${tw._id}-${type}`,
          name,
          documentType: type,
          docType: label,
          reason: doc.reason || '',
          date: doc.updated ? new Date(doc.updated) : null,
          tempWorker: tw,
        });
      });
    });
    rows.sort((a, b) => (b.date || 0) - (a.date || 0));
    return rows;
  }, [validatedWorkers]);

  // ── Filtered lists ──

  const filteredPending = useMemo(() => {
    if (!search.trim()) return pendingWorkers;
    const s = search.toLowerCase().trim();
    return pendingWorkers.filter(tw => searchMatch(tw, s));
  }, [search, pendingWorkers]);

  const filteredDocRows = useMemo(() => {
    if (!search.trim()) return validatedDocRows;
    const s = search.toLowerCase().trim();
    return validatedDocRows.filter(r => r.name.toLowerCase().includes(s) || r.docType.toLowerCase().includes(s));
  }, [validatedDocRows, search]);

  // ── Update handler ──

  useEffect(() => {
    if (!updateTempWorker) return;
    const { tempWorkerId, documentType, action, tempWorker: updatedTempWorker } = typeof updateTempWorker === 'object'
      ? updateTempWorker : { tempWorkerId: updateTempWorker, documentType: null, action: 'accept' };
    const newStatus = action === 'refuse' ? 3 : 2;
    const isComplementaryDocument = documentType === 'visa_complementary';
    const isTarget = (tw) => String(tw?._id) === String(tempWorkerId);

    const applyUpdate = (tw) => {
      const updated = { ...tw };
      if (isComplementaryDocument) {
        // The list item carries its own user fields: only the proofs are taken from the answer.
        updated.visa = {
          ...updated.visa,
          complementaryDocuments:
            updatedTempWorker?.visa?.complementaryDocuments ?? updated.visa?.complementaryDocuments ?? [],
        };
      } else if (documentType && updated[documentType]) {
        updated[documentType] = { ...updated[documentType], validated: newStatus, updated: Date.now() };
      }
      return updated;
    };

    // The card shows its new progress while it folds away, then leaves the list.
    const target = pendingWorkers.find(isTarget);
    setPendingWorkers(prev => prev.map(tw => (isTarget(tw) ? applyUpdate(tw) : tw)));
    if (target && !isPendingForJobbiz(applyUpdate(target))) {
      leave(String(tempWorkerId), () =>
        setPendingWorkers(prev => prev.filter(tw => !isTarget(tw) || isPendingForJobbiz(tw)))
      );
    }

    // The AI-validated tab never lists extension proofs.
    if (action !== 'refuse' && !isComplementaryDocument) getValidatedWorkers();
    setUpdateTempWorker(false);
  }, [updateTempWorker]);

  // ── Licence decision taken in the verification modal ──

  useEffect(() => {
    if (!licenceVerificationDecision) return;
    const { tempWorkerId, licenceId, action, licence } = licenceVerificationDecision;
    const isTarget = (row) => String(row?.tempWorkerId) === String(tempWorkerId);
    const applyDecision = (row) => ({
      ...row,
      licences: applyLicenceDecision(row.licences, { licenceId, action, licence }),
    });
    const hasPendingLicence = (row) => (row?.licences || []).some(isLicencePending);

    if (isLicence && activeTab === 'pending') {
      // The card shows its new progress while it folds away, then leaves the list.
      const target = licenceList.rows.find(isTarget);
      setLicenceRows((prev) => prev.map((row) => (isTarget(row) ? applyDecision(row) : row)));
      if (target && !hasPendingLicence(applyDecision(target))) {
        leave(String(tempWorkerId), () =>
          setLicenceRows((prev) => prev.filter((row) => !isTarget(row) || hasPendingLicence(row)))
        );
      }
    }
    if (isLicence) refreshLicenceCounts();
    setLicenceVerificationDecision(null);
  }, [licenceVerificationDecision]);

  // ── Pagination ──

  const isPending = activeTab === 'pending';
  const currentList = isPending ? filteredPending : filteredDocRows;
  const currentIsWaiting = isPending ? isWaiting : isWaitingValidated;
  const totalPages = Math.ceil(currentList.length / LIMIT);
  const startIdx = (currentPage - 1) * LIMIT;
  const paginated = currentList.slice(startIdx, startIdx + LIMIT);

  const pendingDocCount = filteredPending.reduce((s, tw) => s + countPendingDocs(tw), 0);
  const paginationText = isPending
    ? `Affichage de ${paginated.length} intérimaire${paginated.length > 1 ? 's' : ''} sur ${currentList.length} (${pendingDocCount} document${pendingDocCount > 1 ? 's' : ''} à vérifier au total)`
    : `Affichage de ${paginated.length} document${paginated.length > 1 ? 's' : ''} sur ${currentList.length}`;

  const licenceShown = licenceList.rows.length;
  const licenceFooter = isPending
    ? fillTemplate(text(licenceShown > 1 ? 'footer.pendingMany' : 'footer.pendingOne'), {
        shown: licenceShown,
        total: licenceList.total,
        pending: licenceList.totalPendingLicences,
      })
    : fillTemplate(text('footer.validated'), { shown: licenceShown, total: licenceList.total });

  const footer = isLicence
    ? {
        isWaiting: licenceList.isLoading || licenceList.hasError,
        countTotal: licenceList.total,
        countPerPage: LIMIT,
        totalPages: Math.ceil(licenceList.total / LIMIT),
        text: licenceFooter,
      }
    : {
        isWaiting: currentIsWaiting,
        countTotal: currentList.length,
        countPerPage: paginated.length,
        totalPages,
        text: paginationText,
      };

  // ── Tabs config ──

  const tabs = [
    {
      id: 'pending',
      label: text('tabs.pending', 'En attente'),
      count: isLicence ? licenceList.counts.pending : pendingWorkers.length,
    },
    {
      id: 'validated',
      label: text('tabs.validated', 'Validé par IA'),
      count: isLicence ? licenceList.counts.validatedByAI : validatedDocRows.length,
    },
  ];

  const documentTypeOptions = [
    {
      value: 'identity',
      label: text('documentType.identity', 'Identité'),
      description: text('documentType.identityDescription'),
      icon: <CardIdentity wh={18} color="#374151" />,
      iconClassName: 'bg-white',
    },
    {
      value: 'licence',
      label: text('documentType.licence', 'Permis/CACES'),
      description: text('documentType.licenceDescription'),
      icon: <Forklift wh={18} color="#059669" />,
      iconClassName: 'bg-green-50',
    },
  ];

  // ── Actions ──

  const openTempWorkerInfo = (tw) => {
    const userId = tw?.id_user || tw?.user?._id || tw?._id;
    if (userId) { setObjID(userId); toggleSlideOver('get_temp_worker'); }
  };

  const openDocumentModal = (row) => {
    const tw = row.tempWorker;
    if (!tw?._id || !tw?.id_user) return;
    setAdminTempWorkerId(tw._id);
    setAdminUserId(tw.id_user);
    setAdminType(row.documentType);
    const modal = MODAL_MAP[row.documentType];
    if (modal) toggle(modal);
  };

  const countAcceptedFiles = (tw) => {
    const { accepted, total } = countIdentityDocumentProgress(tw);
    const complementaryDocuments = getDisplayableComplementaryDocuments(tw);
    const handledComplementary = complementaryDocuments.filter(
      (document) => String(document.validated) !== '1'
    ).length;
    return {
      accepted: accepted + handledComplementary,
      total: total + complementaryDocuments.length,
    };
  };

  const handleTabChange = (tabId) => {
    setTab(tabId);
    setSearch('');
    setCurrentPage(1);
  };

  const handleTypeChange = (nextType) => {
    setType(nextType);
    setSearch('');
    setCurrentPage(1);
  };

  // The modal reads the temp worker and the entry from the context, and loads the rest itself.
  const openLicenceVerification = (tempWorkerRow, licenceEntry) => {
    if (!tempWorkerRow?.tempWorkerId || !licenceEntry?._id) return;
    setAdminTempWorkerId(tempWorkerRow.tempWorkerId);
    setAdminUserId(tempWorkerRow.userId);
    setLicenceVerificationTempWorker(tempWorkerRow);
    setLicenceVerificationLicence(licenceEntry);
    toggle(LICENCE_VERIFICATION_MODAL);
  };

  const openLicenceTempWorkerInfo = (tempWorkerRow) => {
    if (tempWorkerRow?.userId) { setObjID(tempWorkerRow.userId); toggleSlideOver('get_temp_worker'); }
  };

  const toValidatedLicenceEntry = (row) => ({
    _id: row.licenceId,
    licence: row.licence,
    category: row.category ?? null,
    expiration: row.expiration ?? null,
    validation: 1,
    validatedBy: 'ai',
    validatedAt: row.validatedAt ?? null,
    ai: row.aiReason ? { reason: row.aiReason } : null,
  });

  // ── Render ──

  // Keyed by type and tab so the cascade replays on each switch: each list
  // shows different cards, which remounted anyway.
  const cascade = (index) => ({
    className: 'motion-safe:animate-fadeUp',
    style: { animationDelay: `${Math.min(index, 9) * 30}ms` },
  });

  const renderLicenceContent = () => {
    if (licenceList.isLoading) return <CardSkeleton />;
    if (licenceList.hasError) {
      return (
        <EmptyState
          type="identity_validation"
          title={text('error.title')}
          description={text('error.description')}
          action={{ label: text('error.retry'), onClick: licenceList.reload }}
        />
      );
    }
    if (licenceList.rows.length === 0) {
      const emptyKey = debouncedSearch ? 'search' : isPending ? 'pending' : 'validated';
      return (
        <EmptyState
          type="identity_validation"
          title={text(`empty.${emptyKey}Title`)}
          description={text(`empty.${emptyKey}Description`)}
        />
      );
    }

    return (
      <div key={`${type}-${activeTab}`} className="flex flex-col gap-3 p-4 md:p-6 lg:p-6">
        {isPending
          ? licenceList.rows.map((row, index) => {
              const id = String(row.tempWorkerId);
              const leaving = isLeaving(id);
              const { className: cascadeClassName, style: cascadeStyle } = cascade(index);
              return (
                <LeavingItem
                  key={id}
                  leaving={leaving}
                  onAnimationEnd={onLeaveEnd(id)}
                  className={`[--leave-gap:0.75rem] ${leaving ? '' : cascadeClassName}`}
                  style={cascadeStyle}
                >
                  <LicenceValidationMobileCard
                    tempWorker={row}
                    onOpenTempWorker={openLicenceTempWorkerInfo}
                    onVerifyLicence={(entry) => openLicenceVerification(row, entry)}
                  />
                </LeavingItem>
              );
            })
          : licenceList.rows.map((row, index) => (
              <div key={`${row.tempWorkerId}-${row.licenceId}`} {...cascade(index)}>
                <ValidatedDocumentCard
                  name={getTempWorkerName(row)}
                  docType={getLicenceLabel(row, language)}
                  reason={row.aiReason || ''}
                  date={row.validatedAt ? new Date(row.validatedAt) : null}
                  onClick={() => openLicenceVerification(row, toValidatedLicenceEntry(row))}
                />
              </div>
            ))}
      </div>
    );
  };

  const renderContent = () => {
    if (isLicence) return renderLicenceContent();
    if (currentIsWaiting) return <CardSkeleton />;
    if (paginated.length === 0) return <EmptyState type="identity_validation" />;

    return (
      <div key={`${type}-${activeTab}`} className="flex flex-col gap-3 p-4 md:p-6 lg:p-6">
        {isPending
          ? paginated.map((tw, index) => {
              const { accepted, total } = countAcceptedFiles(tw);
              const leaving = isLeaving(String(tw._id));
              const { className: cascadeClassName, style: cascadeStyle } = cascade(index);
              return (
                <LeavingItem
                  key={tw._id}
                  leaving={leaving}
                  onAnimationEnd={onLeaveEnd(String(tw._id))}
                  className={`[--leave-gap:0.75rem] ${leaving ? '' : cascadeClassName}`}
                  style={cascadeStyle}
                >
                  <IdentityValidationMobileCard
                    tempWorker={tw}
                    nbFiles={accepted}
                    nbFilesTotal={total}
                    onOpenTempWorker={openTempWorkerInfo}
                  />
                </LeavingItem>
              );
            })
          : paginated.map((row, index) => (
              <div key={row.key} {...cascade(index)}>
                <ValidatedDocumentCard
                  name={row.name}
                  docType={row.docType}
                  reason={row.reason}
                  date={row.date}
                  onClick={() => openDocumentModal(row)}
                />
              </div>
            ))}
      </div>
    );
  };

  return (
    <PageForm>
      <SubNavbar elevated={isListScrolled}>
        <div className="flex justify-between items-center w-full gap-3">
          <div className="flex flex-1 items-center gap-3 min-w-0">
            <div className="flex-1 max-w-[400px]">
              <StyledInput
                type="text"
                placeholder="Rechercher..."
                imgFront={<SearchLoop wh={16} color="#6B7280" />}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <DocumentTypeSelect
              options={documentTypeOptions}
              value={type}
              onChange={handleTypeChange}
              ariaLabel={text('documentType.label')}
              className="shrink-0"
            />
          </div>
          <PillTabs variant="counter" tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </div>
      </SubNavbar>

      <div className="flex flex-col w-full h-full overflow-hidden">
        <div ref={listScrollRef} className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </div>

      {!footer.isWaiting && footer.totalPages >= 1 && (
        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-8 py-4">
          <Pagination
            currentPage={currentPage}
            countPerPage={footer.countPerPage}
            countTotal={footer.countTotal}
            totalPages={footer.totalPages}
            setCurrentPage={setCurrentPage}
            customText={footer.text}
          />
        </div>
      )}
    </PageForm>
  );
};

export default IdentityValidation;
